import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Logo } from '@/components/ui/Logo';
import { 
  LayoutDashboard, Package, Users, FileText, Wrench, 
  ShoppingCart, ClipboardList, UserCog, AlertTriangle,
  Activity, Sun, Moon, LogOut, Menu, X, Settings as SettingsIcon,
  BarChart3, CreditCard, FileSignature, Receipt, Cpu, Wallet, Barcode, Building2
} from 'lucide-react';
import { cn } from '@/lib/utils';

const NavItem = ({ icon: Icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'relative w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium transition-all duration-200',
      active 
        ? 'text-[var(--text-primary)] bg-[var(--surface-hover)]'
        : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]/60'
    )}
    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {active && (
      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-5 rounded-r bg-[var(--brand-cyan)]" />
    )}
    <Icon size={16} strokeWidth={1.75} />
    <span className="tracking-tight">{label}</span>
  </button>
);

export const Layout = ({ children, activeTab, onTabChange }) => {
  const { user, logout, isAdmin, isSuperAdmin, isTechnician, canViewFinancials, canManageSettings } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Technicians have a focused view: only RMAs, Repairs, Customers, Products
  const tech = isTechnician();
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, show: !tech },
    { id: 'products', label: 'Products', icon: Package, show: true },
    { id: 'serial-lookup', label: 'Serial Lookup', icon: Barcode, show: true },
    { id: 'customers', label: 'Customers', icon: Users, show: true },
    { id: 'quotations', label: 'Quotations', icon: FileSignature, show: !tech },
    { id: 'invoices', label: 'Invoices', icon: FileText, show: !tech },
    { id: 'repairs', label: 'Repairs', icon: Wrench, show: true },
    { id: 'rmas', label: 'RMAs', icon: AlertTriangle, show: true },
    { id: 'rma-replacement-log', label: 'RMA Replacement Log', icon: FileSignature, show: !tech },
    { id: 'builds', label: 'PC Builds', icon: Cpu, show: true },
    { id: 'credit-notes', label: 'Credit Notes', icon: CreditCard, show: !tech },
    { id: 'dealer-credit', label: 'Dealer Credit', icon: Wallet, show: !tech },
    { id: 'expenses', label: 'Expenses', icon: Receipt, show: !tech },
    { id: 'grns', label: 'GRNs', icon: ClipboardList, show: !tech },
    { id: 'suppliers', label: 'Suppliers', icon: Building2, show: !tech },
    { id: 'reports', label: 'Reports', icon: BarChart3, show: canViewFinancials() },
    { id: 'blacklist', label: 'Blacklist', icon: ShoppingCart, show: isAdmin() },
    { id: 'users', label: 'Users', icon: UserCog, show: isSuperAdmin() },
    { id: 'activity', label: 'Activity', icon: Activity, show: isAdmin() },
    { id: 'settings', label: 'Settings', icon: SettingsIcon, show: canManageSettings() }
  ];

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Mobile menu */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)] shadow-sm"
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={cn(
        'fixed top-0 left-0 h-full w-60 z-40 transition-transform',
        'bg-[var(--surface)] border-r border-[var(--border)]',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}>
        <div className="flex flex-col h-full">
          {/* Brand */}
          <div className="px-5 py-5 border-b border-[var(--border)]">
            <div className="flex items-center gap-3">
              <Logo size="xs" />
              <div>
                <h1 className="text-[15px] font-semibold tracking-tight text-[var(--text-primary)]">PC NERDS</h1>
                <p className="text-[9px] tracking-[0.2em] uppercase text-[var(--text-muted)] mt-0.5">Inventory OS</p>
              </div>
            </div>
            {user && (
              <div className="mt-4 pt-4 border-t border-[var(--border)]">
                <p className="text-[11px] text-[var(--text-muted)] uppercase tracking-wider">Signed in as</p>
                <p className="text-[13px] font-medium text-[var(--text-primary)] truncate mt-0.5">{user.full_name}</p>
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mt-0.5">{user.role}</p>
              </div>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {navItems.filter(item => item.show).map(item => (
              <NavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                active={activeTab === item.id}
                onClick={() => {
                  onTabChange(item.id);
                  setSidebarOpen(false);
                }}
              />
            ))}
          </nav>

          {/* Footer */}
          <div className="px-3 py-3 border-t border-[var(--border)] space-y-0.5">
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)] transition-colors"
              data-testid="theme-toggle"
            >
              {isDark ? <Sun size={16} strokeWidth={1.75} /> : <Moon size={16} strokeWidth={1.75} />}
              <span className="tracking-tight">{isDark ? 'Light mode' : 'Dark mode'}</span>
            </button>
            <button
              onClick={logout}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium text-[var(--text-muted)] hover:text-[var(--status-danger)] hover:bg-[var(--status-danger)]/10 transition-colors"
              data-testid="logout-button"
            >
              <LogOut size={16} strokeWidth={1.75} />
              <span className="tracking-tight">Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      <main className="lg:ml-60 min-h-screen">
        <div className="p-8 lg:p-10 max-w-[1600px] mx-auto">
          {children}
        </div>
      </main>

      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};
